// Compatible IFC class set for the IFCEdit "Classe" inline editor.
//
// The classes listed here all share the SAME STEP attribute layout:
//
//   0 GlobalId, 1 OwnerHistory, 2 Name, 3 Description, 4 ObjectType,
//   5 ObjectPlacement, 6 Representation, 7 Tag, 8 PredefinedType
//
// They're all IfcBuildingElement subclasses (or close cousins that
// share the same layout). Because the layout is identical, the STEP
// patcher can safely rewrite `#42=IFCWALL(...)` → `#42=IFCSLAB(...)`
// without rearranging any arguments.
//
// What stays consistent after a class change:
//   * GlobalId, Name, Description, Tag, ObjectType, ObjectPlacement,
//     Representation: same values, same positions.
//   * IfcRelContainedInSpatialStructure / IfcRelAssociatesMaterial /
//     IfcRelAggregates / etc.: they reference the entity by #id, not
//     by class, so they keep working.
//
// What CAN become inconsistent and we accept as a trade-off:
//   * IfcRelDefinesByType pointing at an IfcWallType from what's now
//     an IfcSlab. Most viewers still open the file; strict validators
//     complain. The user can fix the type assignment manually after.
//   * PredefinedType: each class has its own enum (IfcWallTypeEnum vs
//     IfcSlabTypeEnum). The UI auto-resets PredefinedType to
//     NOTDEFINED when the existing value isn't valid in the new
//     class's enum; see PropertiesPanel.
//
// Classes deliberately EXCLUDED from this set:
//   * IfcDoor / IfcWindow: extra OverallHeight/OverallWidth args
//     before PredefinedType, layout mismatch.
//   * IfcStairFlight / IfcRampFlight: extra NumberOf* / Riser* args.
//   * IfcOpeningElement: IfcFeatureElementSubtraction layout.
//   * IfcSpace / IfcBuildingStorey / IfcSite / IfcBuilding: these
//     are IfcSpatialElement, not IfcBuildingElement. They use
//     LongName + CompositionType, different layout.
//   * MEP elements (IfcDuctSegment, IfcPipeSegment, etc.): share the
//     same attribute layout but they're IfcDistributionElement (not
//     IfcBuildingElement). The user typically doesn't want to convert
//     a Wall into a DuctSegment, so we keep them in their own group
//     (not yet implemented; extend the table if asked).

const COMPATIBLE_BUILDING_ELEMENTS: readonly string[] = [
  'IfcWall',
  'IfcWallStandardCase',
  'IfcWallElementedCase',
  'IfcCurtainWall',
  'IfcSlab',
  'IfcSlabStandardCase',
  'IfcSlabElementedCase',
  'IfcBeam',
  'IfcBeamStandardCase',
  'IfcColumn',
  'IfcColumnStandardCase',
  'IfcMember',
  'IfcMemberStandardCase',
  'IfcPlate',
  'IfcPlateStandardCase',
  'IfcRoof',
  'IfcCovering',
  'IfcStair',
  'IfcRamp',
  'IfcRailing',
  'IfcFooting',
  'IfcPile',
  'IfcChimney',
  'IfcShadingDevice',
  'IfcBuildingElementProxy',
]

const COMPATIBLE_LOWER = new Set(
  COMPATIBLE_BUILDING_ELEMENTS.map((c) => c.toLowerCase()),
)

const CANONICAL_BY_LOWER: Record<string, string> = COMPATIBLE_BUILDING_ELEMENTS.reduce(
  (acc, c) => {
    acc[c.toLowerCase()] = c
    return acc
  },
  {} as Record<string, string>,
)

/** Canonicalise an IFC class name (UPPERCASE or mixed) into the
 *  camel-case form used by the compatibility table. Returns null
 *  when the class isn't in the compatible set. */
export function canonicaliseCompatibleClass(name: string): string | null {
  if (!name) return null
  return CANONICAL_BY_LOWER[name.toLowerCase()] ?? null
}

/** True iff `className` belongs to the compatible-class set, so it
 *  can be the source OR the target of a class change. */
export function isClassChangeable(className: string): boolean {
  return COMPATIBLE_LOWER.has(className.toLowerCase())
}

/** Returns the list of classes the user can switch `currentClass`
 *  to (excluding `currentClass` itself). Returns [] when
 *  `currentClass` isn't in the compatible set. Sorted alphabetically
 *  for stable UI ordering. */
export function getCompatibleClasses(currentClass: string): readonly string[] {
  const canon = canonicaliseCompatibleClass(currentClass)
  if (!canon) return []
  return COMPATIBLE_BUILDING_ELEMENTS.filter((c) => c !== canon).slice().sort()
}

/** Full sorted list of compatible classes, including the source.
 *  Used to populate dropdowns where the current class should remain
 *  visible (so editing back to the source is possible). */
export function getCompatibleClassesIncludingSelf(
  currentClass: string,
): readonly string[] {
  if (!isClassChangeable(currentClass)) return []
  return [...COMPATIBLE_BUILDING_ELEMENTS].sort()
}
